import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Paper,
  Checkbox,
  Tooltip,
  TextField
} from "@mui/material"; 
import TableContainer from "@mui/material/TableContainer";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DownloadIcon from "@mui/icons-material/Download";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import { useTranslate } from "src/locales";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ByzToggleIconButton from "src/components/core-components/mui/buttons/ByzToggleIconButton.tsx";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";

interface CustomToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

function CustomToolbar({ searchTerm, onSearchChange }: CustomToolbarProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Export">
          <IconButton size="small">
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Column visibility">
          <IconButton size="small">
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Density">
          <IconButton size="small">
            <DensityMediumIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Filter">
          <IconButton size="small">
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
        <TextField
          placeholder="Ara"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: '#00B8D9', mr: 1 }} fontSize="small" />,
          }}
          sx={{ width: 200 }}
        />
      </Box>
    </Box>
  );
}

export interface IsolationOption {
  id: number;
  icon: React.ReactNode;
  iconSrc?: string;
  name: string;
  description: string;
  elHijyeni: string;
  eldiven: string;
  onluk: string;
  maske: string;
  gozluk: string;
  hastaninTransferi: string;
}

const isolationOptions: IsolationOption[] = [
  {
    id: 1,
    iconSrc: "/assets/icons/isolation/std_precautions.svg",
    icon: null,
    name: "Standart Önlemler",
    description: "Küçük partiküllerin >5pm damlacıkların geçişini önlemesinde kullanılır. Küçük damlacıkların geçişini önlemesinde kullanılır.",
    elHijyeni: "El Yıkama Alkol Bazlı El Antiseptik",
    eldiven: "Sıçrama Riski Olduğunda",
    onluk: "Sıçrama Riski Olduğunda",
    maske: "N95",
    gozluk: "Sıçrama Riski Olduğunda",
    hastaninTransferi: "Hastaya Cerrahi Maske Ayrılır",
  },
  {
    id: 2,
    iconSrc: "/assets/icons/isolation/contact_precautions.svg",
    icon: null,
    name: "Temas Önlemleri",
    description: "Küçük partiküllerin >5pm damlacıkların geçişini önlemesinde kullanılır. Küçük damlacıkların geçişini önlemesinde kullanılır.",
    elHijyeni: "El Yıkama Alkol Bazlı El Antiseptik",
    eldiven: "Sıçrama Riski Olduğunda",
    onluk: "Sıçrama Riski Olduğunda",
    maske: "N95",
    gozluk: "Sıçrama Riski Olduğunda",
    hastaninTransferi: "Hastaya Cerrahi Maske Ayrılır",
  },
  {
    id: 3,
    iconSrc: "/assets/icons/isolation/resp_isolation.svg",
    icon: null,
    name: "Solunum İzolasyonu",
    description: "Küçük partiküllerin >5pm damlacıkların geçişini önlemesinde kullanılır. Küçük damlacıkların geçişini önlemesinde kullanılır.",
    elHijyeni: "El Yıkama Alkol Bazlı El Antiseptik",
    eldiven: "Sıçrama Riski Olduğunda",
    onluk: "Sıçrama Riski Olduğunda",
    maske: "N95",
    gozluk: "Sıçrama Riski Olduğunda",
    hastaninTransferi: "Hastaya Cerrahi Maske Ayrılır",
  },
  {
    id: 4,
    iconSrc: "/assets/icons/isolation/droplet_isolation.svg",
    icon: null,
    name: "Damlacık İzolasyonu",
    description: "Küçük partiküllerin >5pm damlacıkların geçişini önlemesinde kullanılır. Küçük damlacıkların geçişini önlemesinde kullanılır.",
    elHijyeni: "El Yıkama Alkol Bazlı El Antiseptik",
    eldiven: "Sıçrama Riski Olduğunda",
    onluk: "Sıçrama Riski Olduğunda",
    maske: "N95",
    gozluk: "Sıçrama Riski Olduğunda",
    hastaninTransferi: "Hastaya Cerrahi Maske Ayrılır",
  },
  {
    id: 5,
    iconSrc: "/assets/icons/isolation/strict_contact.svg",
    icon: null,
    name: "Sıkı Temas Önlemleri",
    description: "Küçük partiküllerin >5pm damlacıkların geçişini önlemesinde kullanılır. Küçük damlacıkların geçişini önlemesinde kullanılır.",
    elHijyeni: "El Yıkama Alkol Bazlı El Antiseptik",
    eldiven: "Sıçrama Riski Olduğunda",
    onluk: "Sıçrama Riski Olduğunda",
    maske: "N95",
    gozluk: "Sıçrama Riski Olduğunda",
    hastaninTransferi: "Hastaya Cerrahi Maske Ayrılır",
  },
  {
    id: 6,
    iconSrc: "/assets/icons/isolation/strict_contact.svg",
    icon: null,
    name: "Koruyucu Ortam",
    description: "Küçük partiküllerin >5pm damlacıkların geçişini önlemesinde kullanılır. Küçük damlacıkların geçişini önlemesinde kullanılır.",
    elHijyeni: "El Yıkama Alkol Bazlı El Antiseptik",
    eldiven: "Sıçrama Riski Olduğunda",
    onluk: "Sıçrama Riski Olduğunda",
    maske: "N95",
    gozluk: "Sıçrama Riski Olduğunda",
    hastaninTransferi: "Hastaya Cerrahi Maske Ayrılır",
  },
];

interface IsolationSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (isolations: IsolationOption[]) => void;
}

const IsolationSelectionDialog: React.FC<IsolationSelectionDialogProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslate();
  const [selectedIsolations, setSelectedIsolations] = useState<number[]>([]);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isEyeOpen, setIsEyeOpen] = useState<boolean>(true);
  const [isArrowUp, setIsArrowUp] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleToggleIsolation = (id: number) => {
    setSelectedIsolations((prev) => {
      if (prev.includes(id)) {
        return prev.filter((isolationId) => isolationId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIsolations(isolationOptions.map((option) => option.id));
    } else {
      setSelectedIsolations([]);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const dialogStyle = isFullscreen
    ? {
        maxWidth: "100%",
        maxHeight: "100%",
        width: "100%",
        height: "100%",
        margin: 0,
      }
    : {
        maxWidth: "lg",
      };

  const filteredOptions = isolationOptions.filter(option => 
    option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleConfirmSelection = () => {
    const selectedOptions = isolationOptions.filter(option => 
      selectedIsolations.includes(option.id)
    );
    onSelect(selectedOptions);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: dialogStyle,
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">{t("isolation-requests.isolation-selection.title")}</Typography>
        <Box>
          <Tooltip title={t("buttons.fullscreen")}>
            <IconButton onClick={toggleFullscreen}>
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("buttons.close")}>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <Box sx={{ px: 2, py: 1, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
        <CustomToolbar 
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />
      </Box>

      <DialogContent dividers sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: isFullscreen ? "calc(100vh - 200px)" : 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIsolations.length === isolationOptions.length}
                    indeterminate={
                      selectedIsolations.length > 0 &&
                      selectedIsolations.length < isolationOptions.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.icon")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.isolation-name")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.description")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.hand-hygiene")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.gloves")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.gown")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.mask")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.eye-protection")}</TableCell>
                <TableCell>{t("isolation-requests.isolation-selection.columns.patient-transfer")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOptions.map((option) => (
                <TableRow
                  key={option.id}
                  hover
                  selected={selectedIsolations.includes(option.id)}
                  onClick={() => handleToggleIsolation(option.id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={selectedIsolations.includes(option.id)} />
                  </TableCell>
                  <TableCell>
                    {option.iconSrc && (
                      <Box
                        sx={{
                          width: 35,
                          height: 35,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={option.iconSrc}
                          alt={option.name}
                          style={{ maxWidth: "100%", maxHeight: "100%" }}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{option.name}</TableCell>
                  <TableCell>{option.description}</TableCell>
                  <TableCell>{option.elHijyeni}</TableCell>
                  <TableCell>{option.eldiven}</TableCell>
                  <TableCell>{option.onluk}</TableCell>
                  <TableCell>{option.maske}</TableCell>
                  <TableCell>{option.gozluk}</TableCell>
                  <TableCell>{option.hastaninTransferi}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box>
          <Grid
            container
            sx={{
              margin: "8px",
              p: 2,
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <Typography variant="subtitle2">
              {t("isolation-requests.your-selections")}
            </Typography>

            <ByzMuiButton
              sx={{ marginLeft: "auto" }}
              variant="contained"
              color="primary"
              onClick={handleConfirmSelection}
            >
              <AddIcon />
              <ByzMuiTypography fontSize={10}>
                {t("isolation-requests.add")}
              </ByzMuiTypography>
            </ByzMuiButton>

            <Box>
              <ByzToggleIconButton
                iconOn="/assets/icons/generals/eye-open.svg"
                iconOff="/assets/icons/generals/eye-close.svg"
                altText="eye"
                onClick={() => setIsEyeOpen(!isEyeOpen)}
              />
              <ByzToggleIconButton
                iconOn="/assets/icons/generals/full-screen-off.svg"
                iconOff="/assets/icons/generals/full-screen-on.svg"
                altText="fullscreen"
                onClick={toggleFullscreen}
              />
              <ByzToggleIconButton
                iconOn="/assets/icons/generals/arrow-up.svg"
                iconOff="/assets/icons/generals/arrow-down.svg"
                altText="arrow"
                onClick={() => setIsArrowUp(!isArrowUp)}
              />
            </Box>
          </Grid>

          <Box
            sx={{ flex: 1, width: "100%", height: "100%", overflow: "auto" }}
            hidden={isArrowUp}
          >
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead style={{ display: isEyeOpen ? 'table-header-group' : 'none' }}>
                  <TableRow>
                    <TableCell padding="checkbox" rowSpan={2}>
                      <Checkbox />
                    </TableCell>
                    <TableCell colSpan={1}>{t("isolation-requests.form.isolation")}</TableCell>
                    <TableCell colSpan={2}>{t("isolation-requests.form.isolation-take-reason")}</TableCell>
                    <TableCell colSpan={2}>{t("isolation-requests.form.non-bacterial-isolation-reason")}</TableCell>
                    <TableCell colSpan={3}>{t("isolation-requests.form.isolation-notification")}</TableCell>
                    <TableCell colSpan={5}>{t("isolation-requests.form.isolation-entry")}</TableCell>
                    <TableCell rowSpan={2}>{t("isolation-requests.edit")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t("isolation-requests.form.icon")}</TableCell>
                    
                    <TableCell>{t("isolation-requests.form.isolation-reason")}</TableCell>
                    <TableCell>{t("isolation-requests.form.description")}</TableCell>
                    
                    <TableCell>{t("isolation-requests.form.isolation-reason")}</TableCell>
                    <TableCell>{t("isolation-requests.form.description")}</TableCell>
                    
                    <TableCell>{t("isolation-requests.form.microbiology")}</TableCell>
                    <TableCell>{t("isolation-requests.form.kitchen-team")}</TableCell>
                    <TableCell>{t("isolation-requests.form.cleaning-team")}</TableCell>
                    
                    <TableCell>{t("isolation-requests.start-date")}</TableCell>
                    <TableCell>{t("isolation-requests.end-date")}</TableCell>
                    <TableCell>{t("isolation-requests.form.patient-and-family")}</TableCell>
                    <TableCell>{t("isolation-requests.form.approval-rejection")}</TableCell>
                    <TableCell>{t("isolation-requests.status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedIsolations.map((id) => {
                    const option = isolationOptions.find(opt => opt.id === id);
                    if (!option) return null;
                    
                    return (
                      <TableRow key={`selected-${option.id}`}>
                        <TableCell padding="checkbox">
                          <Checkbox 
                            checked 
                            onChange={() => handleToggleIsolation(option.id)} 
                          />
                        </TableCell>
                        
                        <TableCell>
                          {option.iconSrc && (
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            > 
                              <img
                                src={option.iconSrc}
                                alt={option.name}
                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                              />
                            </Box>
                          )}
                        </TableCell>
                        
                        <TableCell>{option.name}</TableCell>
                        <TableCell>
                          <ByzMuiTextField
                            size="small"
                            defaultValue=""
                            variant="outlined"
                            fullWidth
                            placeholder={t("isolation-requests.form.description")}
                            sx={{ minWidth: "150px" }}
                          />
                        </TableCell>
                        
                        <TableCell>-</TableCell>
                        <TableCell>
                          <ByzMuiTextField
                            size="small"
                            defaultValue=""
                            variant="outlined"
                            fullWidth
                            placeholder={t("isolation-requests.form.description")}
                            sx={{ minWidth: "150px" }}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Checkbox size="small" />
                        </TableCell>
                        <TableCell>
                          <Checkbox size="small" />
                        </TableCell>
                        <TableCell>
                          <Checkbox size="small" />
                        </TableCell>
                        
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>
                          <Checkbox size="small" />
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>-</TableCell>
                        
                        <TableCell>
                          <IconButton size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table> 
            </TableContainer>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default IsolationSelectionDialog;